import { useCallback, useState } from 'react'

import { CheckIcon, ChevronDown, CopyIcon, Info } from 'lucide-react'

import { Tooltip } from '~/components/component-library'
import {
  childrenTextVariants,
  descriptionTextVariants,
  iconVariants,
  labelTextVariants,
  valueTextVariants,
} from '~/components/component-library/LabelValue/styles'
import { LabelValueProps } from '~/components/component-library/LabelValue/types'
import { useCopyValue } from '~/hooks/useCopyValue'
import { cn } from '~/utils/tailwind-utils'

export type { LabelValueProps } from '~/components/component-library/LabelValue/types'

export const LabelValue = ({
  icon,
  label,
  secondaryLabel,
  description,
  value,
  children,
  listIndex,
  isCollapsible,
  defaultOpen = true,
  tooltip,
  size = 'base',
  copyable,
  onClick,
}: LabelValueProps) => {
  const [isCollapsed, setIsCollapsed] = useState(!defaultOpen)
  const { handleCopy, isCopied } = useCopyValue()

  const toggle = useCallback(() => {
    if (isCollapsible) {
      setIsCollapsed((prev) => !prev)
    }
  }, [isCollapsible])

  const copyIconSize = size === 'small' ? 'size-3.5' : 'size-4'
  const hasValue = typeof value === 'string' ? value.length > 0 : value != null

  const content = (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex min-w-0 shrink items-start gap-2">
          {icon && <div className={cn(iconVariants({ size }))}>{icon}</div>}
          <div className={cn(labelTextVariants({ size }), 'min-w-0 flex-1')}>
            <span className="truncate">{label}</span>
            {tooltip && (
              <Tooltip content={tooltip}>
                <Info
                  className={cn(
                    'text-secondary',
                    size === 'small' ? 'my-0.5 size-3' : 'mt-0.5 size-3.5',
                  )}
                />
              </Tooltip>
            )}
            {secondaryLabel && (
              <span
                className={cn(
                  'font-normal text-secondary',
                  size === 'small' ? 'text-sm' : 'text-base',
                )}
              >
                {secondaryLabel}
              </span>
            )}
          </div>
        </div>
        <div
          className={cn(valueTextVariants({ size }), { 'min-w-20': hasValue })}
        >
          {typeof value === 'string' ? (
            <span className="min-w-0 [overflow-wrap:anywhere]">{value}</span>
          ) : (
            value
          )}
          {copyable && (
            <div
              className={cn(
                'flex shrink-0 items-center',
                size === 'small' ? 'h-5' : 'h-6',
              )}
            >
              {isCopied ? (
                <CheckIcon className={cn('text-primary', copyIconSize)} />
              ) : (
                <CopyIcon className={cn('text-primary', copyIconSize)} />
              )}
            </div>
          )}
          {isCollapsible && (
            <div
              className={cn(
                'flex shrink-0 items-center',
                size === 'small' ? 'h-5' : 'h-6',
              )}
            >
              <ChevronDown
                className={cn(
                  'text-secondary transition-all',
                  size === 'small' ? 'size-3.5' : 'size-4',
                  {
                    'rotate-180': !isCollapsed,
                  },
                )}
              />
            </div>
          )}
        </div>
      </div>
      {description && (
        <div className={cn(descriptionTextVariants({ size, hasIcon: !!icon }))}>
          {description}
        </div>
      )}
    </div>
  )

  const onCopyClick = copyable
    ? () => {
        const rawValue =
          typeof copyable === 'string'
            ? copyable
            : typeof value === 'string'
              ? value
              : undefined
        const successMessage =
          typeof label === 'string' ? `Copied ${label.toLowerCase()}` : 'Copied'
        if (rawValue) handleCopy(rawValue, successMessage)
      }
    : undefined

  return (
    <div
      className={cn('flex w-full flex-col gap-2 rounded-lg', {
        'bg-surface-muted': listIndex !== undefined && listIndex % 2 === 0,
        'p-3 py-2.5': listIndex !== undefined,
      })}
    >
      {isCollapsible ? (
        <button
          type="button"
          className="w-full cursor-pointer"
          onClick={toggle}
        >
          {content}
        </button>
      ) : copyable ? (
        <button
          type="button"
          className="w-full cursor-pointer"
          onClick={onCopyClick}
        >
          {content}
        </button>
      ) : onClick ? (
        <div
          role="button"
          tabIndex={0}
          className="w-full cursor-pointer"
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick()
            }
          }}
        >
          {content}
        </div>
      ) : (
        content
      )}
      {!isCollapsed && isCollapsible && !!children && (
        <div className={cn(childrenTextVariants({ size, hasIcon: !!icon }))}>
          {children}
        </div>
      )}
    </div>
  )
}
